/**
 * Find a patient from their receipt — QA report §5.1.
 *
 * A barcode scanner is a keyboard: it types the serial and presses Enter. So
 * this is a plain field that searches on Enter, with the focus kept on it, and
 * a patient's receipt in the hand becomes their open file in one motion. It
 * takes the printed number too ("RC-12-2026-000041") for a receipt whose
 * barcode will not scan.
 */

import { useRef, useState } from 'react';
import { Icon } from '../../components/common';
import { receiptsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';

export function ReceiptLookup({ onFound, placeholder = 'امسح باركود الإيصال أو اكتب السيريال', className = '' }) {
  const { error: toastError } = useNotifications();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const search = async () => {
    const value = code.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const result = await receiptsApi.lookup(value);
      if (!result.patient) {
        toastError('الإيصال لا يخص ملف مريض مسجلاً — سجّل المريض أولاً');
      } else {
        onFound?.(result);
        setCode('');
      }
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };

  return (
    <div className={`lookup-field ${className}`}>
      <Icon name="scan-barcode" size={18} className="shrink-0 text-cyan-300" />
      <input
        ref={input}
        dir="ltr"
        inputMode="numeric"
        className="lookup-field__input"
        placeholder={placeholder}
        aria-label="البحث بسيريال الإيصال"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && search()}
      />
      <button type="button" className="lookup-field__go" onClick={search} disabled={busy || !code.trim()} aria-label="بحث">
        <Icon name={busy ? 'loader-2' : 'search'} size={16} className={busy ? 'animate-spin' : ''} />
      </button>
    </div>
  );
}

export default ReceiptLookup;
